/**
 * Delay a promise-returning function until the caller stops calling it.
 *
 * The header autocomplete searched on every keystroke: typing "viewability"
 * fired nine requests, and Algolia counted nine searches. That inflates every
 * analytics figure the account reports, because "vie" and "viewa" are recorded
 * as searches that a person never meant to run and that mostly return nothing.
 * It is also nine billed operations on a pay-as-you-go plan instead of one.
 *
 * The pattern is Algolia's own: resolve only the most recent call, and let the
 * earlier ones hang rather than reject, so the caller has nothing to catch.
 */
export function debouncePromise<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
  wait: number
): (...args: TArgs) => Promise<TResult> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  return (...args: TArgs) =>
    new Promise<TResult>((resolve) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => resolve(fn(...args)), wait);
    });
}

export default debouncePromise;
